import React, { Dispatch, SetStateAction, useState } from 'react'
import { useParams } from 'react-router'
import { LoadingOverlay, ScrollArea, Table } from "@mantine/core"
import { ColumnDef, genColumnsDef } from './table-helpers'
import { TableHeader } from './TableHeader'
import { TableRow } from './TableRow'
import { Sort, useMarketElements, useReviewVersionMatchSummaries, useVersionRulesCache } from '../../../../../../hooks/global-job-matching'
import { MatchSummaryModal } from '../MatchSummaryModal'
import { ApplyFilterModal } from '../ApplyFilterModal'
import { useDisclosure } from '@mantine/hooks'
import { UseMutationResult } from 'react-query'
import { FieldFilter } from '../../../../../../constants/filter-operators'
import { useAppState } from '../../../../../../hooks/redux'

type ReviewMatchesTableProps = {
  matches: any[]
  matchesLoading: boolean
  isPreviousData: boolean
  sort: Sort
  setSort: Dispatch<SetStateAction<Sort>>
  filters: FieldFilter[]
  setFilters: Dispatch<SetStateAction<FieldFilter[]>>
  setPage: Dispatch<SetStateAction<number>>
  polling: boolean
  runTheRulesMutation: UseMutationResult
  debugEnabled: boolean
}
export const ReviewMatchesTable = ({ matches, matchesLoading, isPreviousData, sort, setSort, polling, filters, setFilters, setPage, runTheRulesMutation, debugEnabled }: ReviewMatchesTableProps) => {
  const params = useParams<{ id: string}>()
  const rules = useVersionRulesCache(params.id)
  const { session: { dynamic_columns} } = useAppState()
  const [hoveredRowId, setHoveredRowId] = useState<any>(null)
  const [matchSummaryModalOpened, { open, close }] = useDisclosure(false)
  const [applyFilterModalOpened, { open: openApplyFilterModal, close: closeApplyFilterModal }] = useDisclosure(false)
  const [selectedMatch, setSelectedMatch] = useState<any>(undefined)
  const [selectedJobMatches, setSelectedJobMatches] = useState<any[]>([])
  const [selectedMatchSummary, setSelectedMatchSummary] = useState<any>(undefined)
  const [editingFilter, setEditingFilter] = useState<ColumnDef | undefined>()
  const { data: marketElements, isLoading: marketElementsLoading } = useMarketElements()
  const { data: matchSummaries } = useReviewVersionMatchSummaries(params.id)

  const handleMatchSelect = (match: any) => {
    if (!debugEnabled) return
    const summary = matchSummaries?.summaries?.find((s: any) => s.org_job_code === match.org_job_code && s.org_job_code_key === match.org_job_code_key)
    const jobMatches = matches?.filter((m: any) => m.org_job_code === match.org_job_code && m.org_job_code_key === match.org_job_code_key)
    setSelectedJobMatches(jobMatches)
    setSelectedMatchSummary(summary)
    setSelectedMatch(match)
    open()
  }

  const handleSort = (col: ColumnDef) => {
    if (col.disableSort) return
    let direction: 'asc' | 'desc' = 'desc'
    if (col?.fieldCode === sort.field) {
      direction = sort.direction === 'asc' ? 'desc' : 'asc'
    }
    setSort({ direction, field: col.fieldCode ?? sort.field })
  }

  const handleFilterClick = (e: React.MouseEvent<SVGSVGElement, MouseEvent>, col: ColumnDef) => {
    setEditingFilter(col)
    e.stopPropagation()
    openApplyFilterModal()
  }

  const columnsDef: ColumnDef[] = genColumnsDef(rules, marketElements, dynamic_columns)
  const columns = columnsDef.map((column, idx) => (
    <TableHeader
      key={idx}
      pl={idx === 0 ? 30 : 0}
      column={column}
      handleSort={handleSort}
      sort={sort}
      filters={filters}
      handleFilterClick={handleFilterClick}
    />
  ))

  const rows = matches?.map((match: any) => (
    <TableRow
      key={match.match_id}
      match={match}
      handleMatchSelect={handleMatchSelect}
      hoveredRowId={hoveredRowId}
      setHoveredRowId={debugEnabled ? setHoveredRowId : () => undefined}
      columnsDef={columnsDef}
      debugEnabled={debugEnabled}
    />
  ));

  return (
    <>
      <MatchSummaryModal
        opened={matchSummaryModalOpened}
        onClose={close} match={selectedMatch}
        matchSummary={selectedMatchSummary}
        jobMatches={selectedJobMatches}
      />
      <ApplyFilterModal
        opened={applyFilterModalOpened}
        filters={filters}
        setFilters={setFilters}
        setPage={setPage}
        onClose={() => {
          setEditingFilter(undefined)
          closeApplyFilterModal()
        }}
        filter={editingFilter}
      />
      <ScrollArea mih={400} h={`calc(100vh - ${!filters?.length ? 430 : 460}px)`}>
        <Table stickyHeader>
          <LoadingOverlay loaderProps={{ size: 110 }} visible={runTheRulesMutation.isLoading || polling || isPreviousData || matchesLoading || marketElementsLoading} />
          <Table.Thead style={{ backgroundColor: 'var(--mantine-color-ghost-5)' }}>
            {columns}
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </ScrollArea>
    </>
  )
}
